import { Spacing } from '@persona-id/design-tokens';
import { ArrowExpandIcon, ArrowRightIcon } from '@persona-id/design-tokens/icons';
import { compact, kebabCase, uniq } from 'lodash/fp';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import CodeEditor from 'dashboard/components-lib/CodeEditor/CodeEditor';
import { AccountEvent } from 'dashboard/models/account';
import Button from 'design-system/components/Button';
import Popover from 'design-system/components/Popover';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';
import { Field, FieldsMap, FieldType, ParsedField, parseField } from 'lib/fields';

const FieldDesc = styled('div')`
  margin-top: ${Spacing[2]};
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${Spacing[1]};
  grid-row-gap: ${Spacing[1]};
  align-items: top;
`;

const FieldKeyDesc = styled('div')`
  display: grid;
  grid-template-columns: auto 20px;
  align-items: center;

  grid-column-gap: ${Spacing[1]};
`;

const FieldValueContainer = styled('div')`
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
  white-space: nowrap;
`;

const FieldValueTooltipContent = styled('div')`
  max-width: 250px;
  padding: ${Spacing[2]};
  overflow-wrap: break-word;
`;

const FieldKeyLabel = styled('div')`
  ${paragraphMixins.md}

  overflow: hidden;
  text-overflow: ellipsis;
`;

const MultiChoiceOptions = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${Spacing[1]};
`;

const getFieldValue = (fieldPath: string[], fields: FieldsMap): Field | null => {
  let currentFieldsMap: FieldsMap = fields;

  for (const key of fieldPath) {
    if (currentFieldsMap[key].type === FieldType.Hash) {
      // Some of the typing is a bit wonky here, but since we check the type to make sure the field is a hash, we
      // know that the type of `value` will be a FieldsMap as well.
      currentFieldsMap = currentFieldsMap[key].value as FieldsMap;
    } else {
      return currentFieldsMap[key];
    }
  }

  return null;
};

interface UpdatedFieldsEventDescription {
  eventId: string;
  eventPayload: AccountEvent;
  fieldsUpdated: string[];
}

const UpdatedFieldsEventDescription = ({
  eventId,
  eventPayload,
  fieldsUpdated,
}: UpdatedFieldsEventDescription) => {
  // Cut off paths with array indexing in them because they look the same in the description
  // and so we want to de-dup them.
  // e.g. ['multi-choice-key.0', 'multi-choice-key.1'] -> ['multi-choice-key']
  const uniqueFieldsUpdated = uniq(
    fieldsUpdated.map((fieldKey) => {
      const match = /\.\d(\.|$)/.exec(fieldKey);
      return fieldKey.substring(0, match?.index);
    }),
  );
  const fieldDescriptions = uniqueFieldsUpdated.map((fieldKey) => {
    const path = fieldKey.split('.').map((key) => kebabCase(key));

    const fieldValue = getFieldValue(path, eventPayload.attributes.fields);

    return (
      <React.Fragment key={`${eventId} - ${fieldKey}`}>
        {fieldValue && <FieldDescription fieldKey={fieldKey} fieldValue={fieldValue} />}
      </React.Fragment>
    );
  });

  return <FieldDesc>{compact(fieldDescriptions)}</FieldDesc>;
};

interface FieldDescriptionProps {
  fieldKey: string;
  fieldValue: Field;
}

const FieldDescription: FC<FieldDescriptionProps> = ({ fieldKey, fieldValue }) => {
  const fieldKeyElementRef = useRef<HTMLDivElement>(null);
  const fieldValueElementRef = useRef<HTMLDivElement>(null);

  const [hideFieldKeyTooltip, setHideFieldKeyTooltip] = useState(false);
  const [hideFieldValueTooltip, setHideFieldValueTooltip] = useState(false);

  useEffect(() => {
    if (!fieldKeyElementRef.current || !fieldValueElementRef.current) return;

    const fieldKeyElement = fieldKeyElementRef.current;
    const fieldValueElement = fieldValueElementRef.current;

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === fieldKeyElement) {
          setHideFieldKeyTooltip(
            (entry.target.clientWidth ?? 0) >= (entry.target.scrollWidth ?? 0),
          );
        }
        if (entry.target === fieldValueElement) {
          setHideFieldValueTooltip(
            (entry.target.clientWidth ?? 0) >= (entry.target.scrollWidth ?? 0),
          );
        }
      });
    });

    resizeObserver.observe(fieldKeyElement);
    resizeObserver.observe(fieldValueElement);

    return () => resizeObserver.disconnect();
  }, []);

  const parsedField = useMemo(() => {
    return parseField(fieldValue);
  }, [fieldValue]);

  const displayField = (field: Field) => {
    if (field.value == null || field.value === '') {
      return <FieldValueContainer ref={fieldValueElementRef}>null</FieldValueContainer>;
    }

    if (field.type === FieldType.File && field.value != null) {
      return (
        <FieldValueContainer ref={fieldValueElementRef}>{field.value.filename}</FieldValueContainer>
      );
    }

    if (field.type === FieldType.MultiChoices) {
      return (
        <MultiChoiceOptions>
          {field.value.map((option: string, optionIndex: number) => (
            <Sticker key={optionIndex} variant='primary'>
              {option}
            </Sticker>
          ))}
        </MultiChoiceOptions>
      );
    }

    const parsedField = parseField(field);

    if (typeof parsedField === 'object') {
      return <FieldValuePopover fieldKey={fieldKey} parsedField={parsedField} />;
    }

    return <FieldValueContainer ref={fieldValueElementRef}>{parsedField}</FieldValueContainer>;
  };

  return (
    <>
      <div>
        <FieldKeyDesc>
          <Tooltip
            disabled={hideFieldKeyTooltip}
            side='top'
            trigger={<FieldKeyLabel ref={fieldKeyElementRef}>{fieldKey}</FieldKeyLabel>}
          >
            <Paragraph>{fieldKey}</Paragraph>
          </Tooltip>
          <ArrowRightIcon />
        </FieldKeyDesc>
      </div>
      <Tooltip disabled={hideFieldValueTooltip} side='top' trigger={displayField(fieldValue)}>
        <FieldValueTooltipContent>{parsedField}</FieldValueTooltipContent>
      </Tooltip>
    </>
  );
};

const PopoverPreview = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacing[1]};
`;

const PopoverContentContainer = styled('div')`
  width: 520px;
  height: 50vh;
`;

const FieldValuePopover = ({
  fieldKey,
  parsedField,
}: {
  fieldKey: string;
  parsedField: Record<string, unknown> | ParsedField[] | null;
}) => {
  const previewText = Array.isArray(parsedField)
    ? parsedField.length > 1
      ? `${parsedField.length} items`
      : '1 item'
    : 'Show details';
  return (
    <Popover
      contentProps={{
        align: 'start',
        showCloseButton: true,
        heading: fieldKey,
        padding: Spacing[2],
      }}
      trigger={
        <PopoverPreview>
          {previewText}
          <Button size='sm' variant='inverse' width='icon' aria-label='Expand'>
            <ArrowExpandIcon />
          </Button>
        </PopoverPreview>
      }
    >
      <PopoverContentContainer>
        <CodeEditor
          language='json'
          code={JSON.stringify(parsedField, null, 2)}
          editable={false}
          readOnly={true}
        />
      </PopoverContentContainer>
    </Popover>
  );
};

export default UpdatedFieldsEventDescription;
